import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import API from "@/lib/api";
import { toTitleCase } from "@/lib/utils";
import { User, Package, Phone, Headphones, Bell, LogOut, ChevronRight, ChevronDown, Monitor, MapPin, Trophy, Edit3, X as XIcon, Loader2, Clock } from "lucide-react";
import { fetchAppConfig } from "@/lib/appConfig";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

// Tier-driven solid metallic gradient header — matches the Digital Card on /app/points
const TIER_HEADER = {
  Silver:   { gradient: "from-[#B6C2D0] via-[#8C99A8] to-[#3A4555]", badge: "bg-[#0A2540]/10", icon: Trophy, label: "SILVER",   darkText: true },
  Gold:     { gradient: "from-[#FBE38B] via-[#D4A93A] to-[#8C6618]", badge: "bg-[#0A2540]/10", icon: Trophy, label: "GOLD",     darkText: true },
  Platinum: { gradient: "from-[#2B50B0] via-[#173075] to-[#0A1736]", badge: "bg-white/20",     icon: Trophy, label: "PLATINUM", darkText: false },
  Diamond:  { gradient: "from-[#A5B4FC] via-[#6366F1] to-[#1E1B4B]", badge: "bg-[#0A2540]/10", icon: Trophy, label: "DIAMOND",  darkText: true },
};

const fmtDateShort = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return iso; }
};

const RowProfile = ({ label, value }) => (
  <div className="flex items-start justify-between gap-3">
    <span className="text-gray-400 uppercase tracking-wider text-[9px] font-semibold flex-shrink-0 pt-0.5">{label}</span>
    <span className="text-gray-800 font-semibold text-right">{value}</span>
  </div>
);

const EditField = ({ label, value, onChange, type = "text", multiline, testid }) => (
  <div>
    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">{label}</label>
    {multiline ? (
      <textarea
        rows={2}
        value={value}
        onChange={e => onChange(e.target.value)}
        className="w-full mt-1 px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-[#008A99] focus:ring-1 focus:ring-[#008A99] resize-none"
        data-testid={testid}
      />
    ) : (
      <input
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        className="w-full mt-1 px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-[#008A99] focus:ring-1 focus:ring-[#008A99]"
        data-testid={testid}
      />
    )}
  </div>
);

export default function ProfilePage() {
  const { user, login, logout } = useAuth();
  const navigate = useNavigate();
  const [showOutstanding, setShowOutstanding] = useState(false);
  const [outstandingData, setOutstandingData] = useState(null); // {total_outstanding, items, registry_id, as_of}
  const [outstandingLoading, setOutstandingLoading] = useState(false);
  const [redemptions, setRedemptions] = useState([]);
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({ full_name: "", email: "", address: "", city: "", state: "" });
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState(null); // { type, text }
  // Mobile-change flow state
  const [mobileChangeOpen, setMobileChangeOpen] = useState(false);
  const [newMobile, setNewMobile] = useState("");
  const [mobileOtp, setMobileOtp] = useState("");
  const [mobileOtpSent, setMobileOtpSent] = useState(false);
  const [mobileSaving, setMobileSaving] = useState(false);
  const [mobileMsg, setMobileMsg] = useState(null);

  const sendMobileChangeOtp = async () => {
    setMobileSaving(true); setMobileMsg(null);
    try {
      await API.post("/profile/mobile/send-otp", { new_mobile: newMobile });
      setMobileOtpSent(true);
      setMobileMsg({ type: "success", text: `OTP sent to ${newMobile}.` });
    } catch (e) {
      setMobileMsg({ type: "error", text: e.response?.data?.detail || "Could not send OTP" });
    } finally { setMobileSaving(false); }
  };

  const verifyMobileChangeOtp = async () => {
    setMobileSaving(true); setMobileMsg(null);
    try {
      const { data } = await API.post("/profile/mobile/verify-otp", { new_mobile: newMobile, otp: mobileOtp });
      if (data.user) {
        const token = localStorage.getItem("auth_token");
        login(data.user, token);
      }
      setMobileMsg({ type: "success", text: "Mobile number updated successfully." });
      setTimeout(() => {
        setMobileChangeOpen(false);
        setMobileOtpSent(false);
        setNewMobile(""); setMobileOtp("");
      }, 1200);
    } catch (e) {
      setMobileMsg({ type: "error", text: e.response?.data?.detail || "OTP verification failed" });
    } finally { setMobileSaving(false); }
  };

  useEffect(() => { API.get("/redemptions").then(r => setRedemptions(r.data || [])).catch(() => {}); }, []);

  // Point 37 & 38 — pull tier config so the profile card colour + tier name match the home card.
  const [appTiers, setAppTiers] = useState([]);
  const [tierDesign, setTierDesign] = useState({});
  useEffect(() => { fetchAppConfig().then(cfg => { setAppTiers(cfg.tiers || []); setTierDesign(cfg.tier_design || {}); }); }, []);

  // Fetch live outstanding from ERP (lazy: only when the user expands the tile)
  const fetchOutstanding = async () => {
    if (outstandingLoading) return;
    setOutstandingLoading(true);
    try {
      const { data } = await API.get("/profile/outstanding");
      setOutstandingData(data);
    } catch {} finally { setOutstandingLoading(false); }
  };
  // Eager fetch once when registry_id is known so the badge value is visible
  // even before the user expands the section.
  useEffect(() => {
    if (user?.registry_id && !outstandingData && !outstandingLoading) {
      fetchOutstanding();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.registry_id]);

  // Lazy-refresh instrument details from ERP if the user has a registry_id but no
  // stored instruments (covers users registered before instrument capture went live).
  const [instrumentsRefreshing, setInstrumentsRefreshing] = useState(false);
  useEffect(() => {
    const hasInstruments = (user?.instruments?.length > 0) ||
      (user?.instrument_data && Object.keys(user.instrument_data).length > 0);
    if (user?.registry_id && !hasInstruments && !instrumentsRefreshing) {
      setInstrumentsRefreshing(true);
      API.post("/profile/instruments/refresh").then(r => {
        const items = r.data?.instruments || [];
        if (items.length > 0) {
          const token = localStorage.getItem("auth_token");
          login({ ...user, instruments: items, instrument_data: items[0] }, token);
        }
      }).catch(() => {}).finally(() => setInstrumentsRefreshing(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.registry_id]);

  const handleLogout = async () => { await logout(); navigate("/login"); };

  const openEdit = () => {
    setEditForm({
      full_name: user?.full_name || "",
      email: user?.email || "",
      address: user?.address || "",
      city: user?.city || "",
      state: user?.state || "",
    });
    setSaveMsg(null);
    setEditOpen(true);
  };

  const saveEdit = async () => {
    setSaving(true); setSaveMsg(null);
    try {
      const { data } = await API.put("/profile", editForm);
      if (data.user) {
        const token = localStorage.getItem("auth_token");
        login(data.user, token);
      }
      setSaveMsg({ type: "success", text: "Profile updated successfully." });
      setTimeout(() => setEditOpen(false), 900);
    } catch (e) {
      setSaveMsg({ type: "error", text: e.response?.data?.detail || "Could not update profile." });
    } finally { setSaving(false); }
  };

  const tier = user?.tier || "Silver";
  const totalEarned = user?.total_points_earned || 0;
  // Point 37 & 38 — mirror the HOME screen card: colour + tier NAME come from the
  // admin config, so the profile card looks identical to the home card.
  const sortedTiers = [...appTiers].sort((a, b) => (a.min_points || 0) - (b.min_points || 0));
  let curTierCfg = null;
  if (sortedTiers.length) {
    let idx = sortedTiers.findIndex(t => (t.name || "").toLowerCase() === (tier || "").toLowerCase());
    if (idx < 0) { idx = 0; for (let i = 0; i < sortedTiers.length; i++) { if (totalEarned >= (sortedTiers[i].min_points || 0)) idx = i; } }
    curTierCfg = sortedTiers[idx];
  }
  const tierName = curTierCfg?.name || tier;
  const design = curTierCfg
    ? { card_image_url: curTierCfg.card_image_url, color_text: curTierCfg.color_text }
    : (tierDesign[tier] || {});
  const customImg = design.card_image_url ? (design.card_image_url.startsWith("http") ? design.card_image_url : `${BACKEND}${design.card_image_url}`) : "";
  const customText = design.color_text || "";
  const tierStyle = TIER_HEADER[tierName] || TIER_HEADER[tier] || TIER_HEADER.Silver;
  const TierIcon = tierStyle.icon;
  const isDarkTier = tierName === "Platinum" || !!customImg;
  const hdrText = isDarkTier ? "text-white" : "text-[#0A2540]";
  const hdrTextDim = isDarkTier ? "text-white/75" : "text-[#0A2540]/70";
  const hdrTextSoft = isDarkTier ? "text-white/65" : "text-[#0A2540]/65";
  const hdrShadow = isDarkTier ? "drop-shadow-[0_1px_2px_rgba(0,0,0,0.35)]" : "drop-shadow-[0_1px_1px_rgba(255,255,255,0.35)]";
  const hdrChip = isDarkTier ? "bg-white/25 text-white ring-1 ring-white/30" : "bg-[#0A2540] text-white";
  const hdrPill = isDarkTier ? "bg-white/15" : "bg-[#0A2540]/10";
  const hdrTextStyle = customText ? { color: customText } : undefined;

  return (
    <div className="space-y-4" data-testid="profile-page">
      {/* Account Header — solid metallic gradient (matches Digital Card) + clickable for edit */}
      <button
        type="button"
        onClick={openEdit}
        className={`w-full text-left rounded-2xl p-5 relative overflow-hidden ${customImg ? "" : `bg-gradient-to-br ${tierStyle.gradient}`} hover:brightness-110 active:brightness-95 transition-all`}
        style={{
          ...(customImg ? { backgroundImage: `url(${customImg})`, backgroundSize: "cover", backgroundPosition: "center" } : {}),
          boxShadow: "inset 0 1px 0 rgba(255,255,255,0.35), 0 6px 18px rgba(0,0,0,0.18)",
        }}
        data-testid={`profile-header-${(tierName || tier).toLowerCase()}`}
      >
        {customImg && <div aria-hidden="true" className="absolute inset-0 bg-black/35" />}
        {/* Decorative blurred orbs */}
        <div aria-hidden="true" className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-10 -mt-10" />
        <div aria-hidden="true" className="absolute bottom-0 left-0 w-20 h-20 bg-white/10 rounded-full -ml-6 -mb-6" />
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none"
          style={{ background: "linear-gradient(180deg, rgba(255,255,255,0.25) 0%, rgba(255,255,255,0) 55%)" }}
        />
        <div className="flex items-center gap-4 relative">
          <div className={`w-16 h-16 ${tierStyle.badge} rounded-full flex items-center justify-center backdrop-blur ring-1 ring-white/20`}>
            <User className={`w-8 h-8 ${hdrText} opacity-80`} />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-0.5 flex-wrap">
              <h2 className={`text-lg font-bold ${hdrText} ${hdrShadow}`} style={hdrTextStyle}>{user?.full_name || "Member"}</h2>
              <span
                className={`inline-flex items-center gap-1 text-[10px] font-black px-2 py-0.5 rounded-full uppercase tracking-wider ${hdrChip}`}
                data-testid="profile-tier-chip"
              >
                <TierIcon className="w-3 h-3" />
                {tierName}
              </span>
            </div>
            <p className={`text-xs ${hdrTextDim}`}>{user?.email || ""}</p>
            <p className={`text-xs ${hdrTextDim}`}>{user?.mobile}</p>
            {user?.registry_id && (
              <div className={`mt-1.5 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full ${hdrPill}`} data-testid="profile-registry-id">
                <span className={`text-[9px] uppercase tracking-wider font-semibold ${hdrTextSoft}`}>Registry ID</span>
                <span className={`text-xs font-bold tracking-wide ${hdrText}`}>{user.registry_id}</span>
              </div>
            )}
          </div>
          <div className={`flex items-center gap-1 ${hdrTextSoft}`}>
            <Edit3 className="w-4 h-4" />
            <ChevronRight className="w-4 h-4" />
          </div>
        </div>
      </button>

      {/* Edit Modal */}
      {editOpen && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={() => !saving && setEditOpen(false)} data-testid="profile-edit-modal">
          <div
            className="bg-white rounded-t-2xl sm:rounded-2xl w-full sm:max-w-md shadow-2xl flex flex-col max-h-[90dvh]"
            style={{ maxHeight: "90vh" }}
            onClick={e => e.stopPropagation()}
          >
            {/* Header — fixed */}
            <div className="flex-shrink-0 flex items-center justify-between p-4 border-b">
              <h3 className="text-base font-bold text-gray-900">Edit Profile</h3>
              <button onClick={() => !saving && setEditOpen(false)} className="p-1 rounded hover:bg-gray-100" data-testid="profile-edit-close">
                <XIcon className="w-4 h-4" />
              </button>
            </div>
            {/* Scrollable body */}
            <div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-3" style={{ WebkitOverflowScrolling: "touch" }}>
              <EditField label="Full Name" value={editForm.full_name} onChange={v => setEditForm(f => ({ ...f, full_name: v }))} testid="edit-full-name" />
              <EditField label="Email" type="email" value={editForm.email} onChange={v => setEditForm(f => ({ ...f, email: v }))} testid="edit-email" />
              <EditField label="Address" value={editForm.address} onChange={v => setEditForm(f => ({ ...f, address: v }))} multiline testid="edit-address" />
              <div className="grid grid-cols-2 gap-3">
                <EditField label="City" value={editForm.city} onChange={v => setEditForm(f => ({ ...f, city: v }))} testid="edit-city" />
                <EditField label="State" value={editForm.state} onChange={v => setEditForm(f => ({ ...f, state: v }))} testid="edit-state" />
              </div>
              <div>
                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Mobile Number</label>
                {!mobileChangeOpen ? (
                  <div className="mt-1 flex items-center gap-2">
                    <input value={user?.mobile || ""} disabled className="flex-1 px-3 py-2.5 border border-gray-200 bg-gray-50 rounded-lg text-sm text-gray-500 cursor-not-allowed" />
                    <button onClick={() => { setMobileChangeOpen(true); setNewMobile(""); setMobileOtp(""); setMobileOtpSent(false); setMobileMsg(null); }} className="px-3 py-2.5 text-xs font-bold rounded-lg border border-[#008A99] text-[#008A99] hover:bg-[#E0F4F5]" data-testid="mobile-change-toggle">Change</button>
                  </div>
                ) : (
                  <div className="mt-1 space-y-2">
                    <input
                      type="tel" inputMode="numeric" maxLength={10}
                      placeholder="Enter new 10-digit mobile"
                      value={newMobile}
                      onChange={e => setNewMobile(e.target.value.replace(/\D/g, ""))}
                      disabled={mobileOtpSent}
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-[#008A99] disabled:bg-gray-50"
                      data-testid="mobile-change-new-input"
                    />
                    {!mobileOtpSent ? (
                      <div className="flex gap-2">
                        <button onClick={sendMobileChangeOtp} disabled={mobileSaving || newMobile.length < 10} className="flex-1 py-2 rounded-lg bg-[#008A99] text-white text-xs font-bold disabled:opacity-50 flex items-center justify-center gap-1" data-testid="mobile-change-send-otp">
                          {mobileSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null} Send OTP
                        </button>
                        <button onClick={() => { setMobileChangeOpen(false); setMobileMsg(null); }} className="px-3 py-2 rounded-lg border border-gray-300 text-xs font-semibold text-gray-700">Cancel</button>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <input
                          type="text" inputMode="numeric" maxLength={6}
                          placeholder="Enter 6-digit OTP"
                          value={mobileOtp}
                          onChange={e => setMobileOtp(e.target.value.replace(/\D/g, ""))}
                          className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-[#008A99] tracking-[0.4em] font-mono"
                          data-testid="mobile-change-otp-input"
                        />
                        <div className="flex gap-2">
                          <button onClick={verifyMobileChangeOtp} disabled={mobileSaving || mobileOtp.length !== 6} className="flex-1 py-2 rounded-lg bg-[#008A99] text-white text-xs font-bold disabled:opacity-50 flex items-center justify-center gap-1" data-testid="mobile-change-verify-otp">
                            {mobileSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null} Verify & Update
                          </button>
                          <button onClick={() => { setMobileOtpSent(false); setMobileOtp(""); }} className="px-3 py-2 rounded-lg border border-gray-300 text-xs font-semibold text-gray-700">Resend</button>
                        </div>
                      </div>
                    )}
                    {mobileMsg && (
                      <div className={`text-xs font-semibold px-2 py-1.5 rounded ${mobileMsg.type === "success" ? "bg-green-50 text-green-700" : "bg-red-50 text-red-600"}`} data-testid="mobile-change-msg">
                        {mobileMsg.text}
                      </div>
                    )}
                  </div>
                )}
                <p className="text-[10px] text-gray-400 mt-1">An OTP will be sent to the new number to confirm the change.</p>
              </div>
              <div>
                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Password</label>
                <div className="mt-1 px-3 py-2.5 border border-gray-200 bg-gray-50 rounded-lg text-sm text-gray-500">
                  Not applicable — you log in via OTP
                </div>
              </div>
              {saveMsg && (
                <div className={`text-xs font-semibold px-3 py-2 rounded-lg ${saveMsg.type === "success" ? "bg-green-50 text-green-700" : "bg-red-50 text-red-600"}`} data-testid="profile-edit-msg">
                  {saveMsg.text}
                </div>
              )}
            </div>
            {/* Footer — fixed */}
            <div
              className="flex-shrink-0 bg-white p-4 border-t flex gap-2"
              style={{ paddingBottom: "calc(1rem + env(safe-area-inset-bottom, 0px))" }}
            >
              <button onClick={() => !saving && setEditOpen(false)} className="flex-1 py-2.5 rounded-lg border border-gray-300 text-sm font-semibold text-gray-700 hover:bg-gray-50" disabled={saving}>Cancel</button>
              <button onClick={saveEdit} disabled={saving} className="flex-1 py-2.5 rounded-lg bg-[#008A99] hover:bg-[#006670] text-white text-sm font-bold flex items-center justify-center gap-1.5 disabled:opacity-50" data-testid="profile-edit-save">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Instrument Details (live from ERP) — full Registry ID details table per screenshot */}
      {instrumentsRefreshing && !(user?.instruments?.length > 0 || (user?.instrument_data && Object.keys(user.instrument_data).length > 0)) && (
        <div className="bg-white rounded-2xl border p-4 flex items-center gap-3" data-testid="profile-instruments-loading">
          <Loader2 className="w-4 h-4 animate-spin text-[#008A99]" />
          <span className="text-xs text-gray-500">Fetching Registry ID details…</span>
        </div>
      )}
      {(user?.instruments?.length > 0 || (user?.instrument_data && Object.keys(user.instrument_data).length > 0)) && (
        <div className="bg-white rounded-2xl border border-emerald-100 shadow-sm overflow-hidden" data-testid="profile-instrument-section">
          {/* Soft mint header — mirrors the "Instrument Found" card the user shared */}
          <div className="bg-emerald-50/60 px-4 py-3 flex items-center gap-2.5 border-b border-emerald-100">
            <div className="w-7 h-7 rounded-full bg-emerald-500 text-white flex items-center justify-center flex-shrink-0">
              <Monitor className="w-4 h-4" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-emerald-700 leading-tight">Instrument{user?.instruments?.length > 1 ? "s" : ""} Found</p>
              {user?.registry_id && (
                <p className="text-[11px] font-semibold text-emerald-600/80">Registry ID: {user.registry_id}</p>
              )}
            </div>
          </div>
          <div className="p-3 sm:p-4 space-y-3">
            {((user.instruments && user.instruments.length > 0) ? user.instruments : [user.instrument_data]).map((inst, idx) => (
              <div key={idx} className="bg-white rounded-xl border border-gray-100" data-testid={`profile-instrument-card-${idx}`}>
                <div className="divide-y divide-gray-100">
                  {inst.PRODUCT_SHORT_NAME && (
                    <div className="flex items-start justify-between px-3 py-2.5">
                      <span className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold">Product</span>
                      <span className="text-sm font-bold text-[#008A99] text-right">{toTitleCase(inst.PRODUCT_SHORT_NAME)}</span>
                    </div>
                  )}
                  {inst.PRODUCT_CATEGORY && (
                    <div className="flex items-start justify-between px-3 py-2.5">
                      <span className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold">Category</span>
                      <span className="text-sm font-bold text-gray-900 text-right">{toTitleCase(inst.PRODUCT_CATEGORY)}</span>
                    </div>
                  )}
                  {inst.PRODUCT_SUB_CATEGORY && (
                    <div className="flex items-start justify-between px-3 py-2.5">
                      <span className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold">Sub-Category</span>
                      <span className="text-sm font-bold text-gray-900 text-right">{toTitleCase(inst.PRODUCT_SUB_CATEGORY)}</span>
                    </div>
                  )}
                  {inst.SERIAL_NUMBER && (
                    <div className="flex items-start justify-between px-3 py-2.5">
                      <span className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold">Serial Number</span>
                      <span className="text-sm font-bold text-gray-900 text-right">{inst.SERIAL_NUMBER}</span>
                    </div>
                  )}
                  {inst.ITEM_CODE && (
                    <div className="flex items-start justify-between px-3 py-2.5">
                      <span className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold">Item Code</span>
                      <span className="text-sm font-bold text-gray-900 text-right">{inst.ITEM_CODE}</span>
                    </div>
                  )}
                  {inst.PARTY_NAME && (
                    <div className="flex items-start justify-between gap-3 px-3 py-2.5">
                      <span className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold flex-shrink-0 pt-0.5">Customer (Party)</span>
                      <span className="text-sm font-bold text-gray-900 text-right break-words">{toTitleCase(inst.PARTY_NAME)}</span>
                    </div>
                  )}
                  {inst.PARTY_NUMBER && (
                    <div className="flex items-start justify-between px-3 py-2.5">
                      <span className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold">Party Number</span>
                      <span className="text-sm font-bold text-gray-900 text-right">{inst.PARTY_NUMBER}</span>
                    </div>
                  )}
                  {inst.CONTRACT_NUMBER && (
                    <div className="flex items-start justify-between px-3 py-2.5">
                      <span className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold">Contract Number</span>
                      <span className="text-sm font-bold text-gray-900 text-right">{inst.CONTRACT_NUMBER}</span>
                    </div>
                  )}
                  {inst.CONTRACT_TYPE && (
                    <div className="flex items-start justify-between px-3 py-2.5">
                      <span className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold">Contract Type</span>
                      <span className="text-sm font-bold text-gray-900 text-right">{inst.CONTRACT_TYPE}</span>
                    </div>
                  )}
                  {inst.CONTRACT_START_DATE && (
                    <div className="flex items-start justify-between px-3 py-2.5">
                      <span className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold">Contract Start</span>
                      <span className="text-sm font-bold text-gray-900 text-right">{fmtDateShort(inst.CONTRACT_START_DATE)}</span>
                    </div>
                  )}
                  {inst.CONTRACT_END_DATE && (
                    <div className="flex items-start justify-between px-3 py-2.5">
                      <span className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold">Contract End</span>
                      <span className="text-sm font-bold text-gray-900 text-right">{fmtDateShort(inst.CONTRACT_END_DATE)}</span>
                    </div>
                  )}
                  {inst.CONTRACT_STATUS && (
                    <div className="flex items-start justify-between px-3 py-2.5">
                      <span className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold">Contract Status</span>
                      <span
                        className={`text-sm font-black text-right uppercase ${
                          String(inst.CONTRACT_STATUS).toUpperCase() === "ACTIVE" ? "text-emerald-600" :
                          String(inst.CONTRACT_STATUS).toUpperCase() === "EXPIRED" ? "text-red-600" :
                          "text-amber-600"
                        }`}
                        data-testid={`profile-contract-status-${idx}`}
                      >
                        {inst.CONTRACT_STATUS}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Menu Items */}
      <div className="bg-white rounded-2xl border divide-y">
        {/* My Redemptions — relocated from Redeem header per user request */}
        <button onClick={() => navigate("/app/redeem?view=history")} className="w-full flex items-center justify-between p-4 text-left" data-testid="profile-my-redemptions-btn">
          <div className="flex items-center gap-3">
            <Package className="w-5 h-5 text-gray-500" />
            <span className="text-sm font-semibold text-gray-900">My Redemptions</span>
            {redemptions.length > 0 && (
              <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-[#E0F4F5] text-[#008A99]" data-testid="profile-redemptions-count">{redemptions.length}</span>
            )}
          </div>
          <ChevronRight className="w-4 h-4 text-gray-400" />
        </button>

        {/* Outstanding — live total from ERP_SUBINV_CHK filtered by registry_id */}
        {(() => {
          const liveItems = outstandingData?.items || [];
          const liveTotal = outstandingData?.total_outstanding ?? null;
          const inr = (n) => `₹ ${Number(n).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
          const fmtDateShort2 = (iso) => {
            if (!iso) return "—";
            try { return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }); }
            catch { return iso; }
          };
          return (
            <>
              <button onClick={() => { setShowOutstanding(!showOutstanding); if (!outstandingData) fetchOutstanding(); }} className="w-full flex items-center justify-between p-4 text-left" data-testid="outstanding-btn">
                <div className="flex items-center gap-3">
                  <Clock className="w-5 h-5 text-gray-500" />
                  <span className="text-sm font-semibold text-gray-900">Outstanding</span>
                  {outstandingLoading && !outstandingData ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin text-gray-400" />
                  ) : liveTotal !== null && liveTotal > 0 ? (
                    <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-[#0A2540] text-white" data-testid="outstanding-amount">{inr(liveTotal)}</span>
                  ) : liveTotal === 0 ? (
                    <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700" data-testid="outstanding-amount-zero">All clear</span>
                  ) : null}
                </div>
                {showOutstanding ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
              </button>
              {showOutstanding && (
                <div className="px-4 py-3 bg-gray-50 space-y-3" data-testid="outstanding-expanded">
                  {/* Live ERP total card */}
                  {liveTotal !== null && (
                    <div className="bg-white rounded-lg p-3 border" data-testid="outstanding-erp-card">
                      <p className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">Total outstanding</p>
                      <p className={`text-xl font-black mt-0.5 ${liveTotal > 0 ? "text-[#0A2540]" : "text-emerald-600"}`}>{inr(liveTotal)}</p>
                      {outstandingData?.as_of && (
                        <p className="text-[10px] text-gray-400 mt-1">Live from ERP · {fmtDateShort2(outstandingData.as_of)}</p>
                      )}
                    </div>
                  )}
                  {outstandingLoading && !liveItems.length && (
                    <div className="py-4 text-center"><Loader2 className="w-4 h-4 animate-spin text-[#008A99] mx-auto" /></div>
                  )}
                  {/* Per-contract breakdown */}
                  {liveItems.length > 0 && (
                    <div className="space-y-2" data-testid="outstanding-contracts">
                      <p className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">Contract breakdown</p>
                      {liveItems.map((it, i) => (
                        <div key={i} className="bg-white rounded-lg p-2.5 border" data-testid={`outstanding-contract-${i}`}>
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0 flex-1">
                              <p className="font-semibold text-sm text-gray-900 truncate">{it.product_short_name || it.item_code}</p>
                              <p className="text-[10px] text-gray-400 truncate">{it.product_category || ""} · SN {it.serial_number || "—"}</p>
                              <p className="text-[10px] text-gray-500 mt-0.5">{it.contract_type || "—"} #{it.contract_number || "—"} · {fmtDateShort2(it.contract_start_date)} → {fmtDateShort2(it.contract_end_date)}</p>
                            </div>
                            <div className="text-right flex-shrink-0">
                              <p className={`text-sm font-black ${it.total_outstanding > 0 ? "text-red-600" : "text-emerald-600"}`}>{inr(it.total_outstanding)}</p>
                              <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full uppercase tracking-wider mt-0.5 inline-block ${
                                String(it.contract_status).toUpperCase() === "ACTIVE" ? "bg-emerald-100 text-emerald-700" :
                                String(it.contract_status).toUpperCase() === "EXPIRED" ? "bg-red-100 text-red-700" :
                                "bg-amber-100 text-amber-700"
                              }`}>{it.contract_status || "—"}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {liveTotal === null && !outstandingLoading && (
                    <p className="text-xs text-gray-400 text-center py-2">Could not load live outstanding right now.</p>
                  )}
                </div>
              )}
            </>
          );
        })()}

        {/* Contact Us */}
        <a href="tel:+911800123456" className="flex items-center gap-3 p-4"><Phone className="w-5 h-5 text-gray-500" /><span className="text-sm font-semibold text-gray-900">Contact us</span></a>

        {/* Support */}
        <button onClick={() => navigate("/app/complaints")} className="w-full flex items-center gap-3 p-4 text-left"><Headphones className="w-5 h-5 text-gray-500" /><span className="text-sm font-semibold text-gray-900">Support</span></button>

        {/* Notification Settings */}
        <div className="flex items-center justify-between p-4">
          <div className="flex items-center gap-3"><Bell className="w-5 h-5 text-gray-500" /><span className="text-sm font-semibold text-gray-900">Notification Settings</span></div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input type="checkbox" defaultChecked className="sr-only peer" />
            <div className="w-9 h-5 bg-gray-200 peer-focus:ring-2 peer-focus:ring-[#008A99]/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-[#008A99]"></div>
          </label>
        </div>
      </div>

      {/* Sign Out */}
      <button onClick={handleLogout} className="w-full flex items-center gap-3 p-4 bg-white rounded-2xl border text-left hover:bg-red-50 transition-colors" data-testid="signout-btn">
        <LogOut className="w-5 h-5 text-gray-500" /><span className="text-sm font-semibold text-gray-900">Sign out</span>
      </button>
    </div>
  );
}
